import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Fuse from "fuse.js";
import useApi from "@/hooks/useApi";
import {
    useCurrentWorkspace,
    useWorkspaceModels,
    useWorkspaceModelsLoading,
    useWorkspaceModelsError,
    useFetchWorkspaceModels
} from "@/store";
import LoadingScreen from "@/components/LoadingScreen";
import { ErrorInfo } from "@/components/ErrorInfo";
import { ModelCard, ModelsHeader } from "@/components/models";
import { IconFrame } from "@components/IconFrame";
import { useDeleteRapidDataOnServer } from "@/store";

const INITIAL_LOAD = 20;
const LOAD_MORE_BATCH = 20;

export const ModelsPage = () => {
    const api = useApi();
    const navigate = useNavigate();
    const currentWorkspace = useCurrentWorkspace();
    const [searchQuery, setSearchQuery] = useState("");
    const [displayedCount, setDisplayedCount] = useState(INITIAL_LOAD);
    const loadMoreRef = useRef<HTMLDivElement>(null);

    const workspaceModels = useWorkspaceModels();
    const workspaceModelsLoading = useWorkspaceModelsLoading();
    const workspaceModelsError = useWorkspaceModelsError();
    const fetchWorkspaceModels = useFetchWorkspaceModels();
    const deleteRapidDataOnServer = useDeleteRapidDataOnServer();

    useEffect(() => {
        if (currentWorkspace?.id) {
            fetchWorkspaceModels(currentWorkspace.id, api);
        }
    }, [currentWorkspace?.id, api, fetchWorkspaceModels]);

    // Setup fuzzy search with Fuse.js - memoized for performance
    const fuse = useMemo(() => {
        return new Fuse(workspaceModels, {
            keys: [
                { name: "workflowName", weight: 2 },
                { name: "datasets.name", weight: 2 },
                { name: "task", weight: 1 }
            ],
            threshold: 0.4,
            includeScore: true,
            includeMatches: true
        });
    }, [workspaceModels]);

    // Memoized filtered models - maintain sort order when no search query
    const filteredModels = useMemo(() => {
        if (!searchQuery.trim()) {
            // Models already sorted by backend when no search
            return workspaceModels;
        }
        // When searching, use fuse.js results (ordered by relevance)
        const results = fuse.search(searchQuery);
        return results.map((result) => result.item);
    }, [searchQuery, fuse, workspaceModels]);

    // Reset displayed count when search changes
    useEffect(() => {
        setDisplayedCount(INITIAL_LOAD);
    }, [searchQuery]);

    // Infinite scroll with Intersection Observer
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && displayedCount < filteredModels.length) {
                    setDisplayedCount((prev) =>
                        Math.min(prev + LOAD_MORE_BATCH, filteredModels.length)
                    );
                }
            },
            { rootMargin: "200px" }
        );

        if (loadMoreRef.current) {
            observer.observe(loadMoreRef.current);
        }

        return () => observer.disconnect();
    }, [displayedCount, filteredModels.length]);

    // Only display the first N models
    const displayedModels = useMemo(() => {
        return filteredModels.slice(0, displayedCount);
    }, [filteredModels, displayedCount]);

    // Memoized handlers for better performance
    const handleModelClick = useCallback(
        (model: any) => {
            if (model.workflowUrl && currentWorkspace?.url) {
                navigate(`/${currentWorkspace.url}/${model.workflowUrl}/deploy`);
            }
        },
        [currentWorkspace?.url, navigate]
    );

    const formatTimestamp = useCallback((timestamp: any) => {
        if (!timestamp) return "";

        // Handle Firestore timestamp format with _seconds
        let date: Date;
        if (timestamp._seconds) {
            date = new Date(timestamp._seconds * 1000);
        } else if (timestamp.seconds) {
            date = new Date(timestamp.seconds * 1000);
        } else {
            date = new Date(timestamp);
        }

        const now = new Date();
        const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

        if (diffInSeconds < 60) return "just now";
        if (diffInSeconds < 3600) {
            const minutes = Math.floor(diffInSeconds / 60);
            return `${minutes} ${minutes === 1 ? "minute" : "minutes"} ago`;
        }
        if (diffInSeconds < 86400) {
            const hours = Math.floor(diffInSeconds / 3600);
            return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
        }
        if (diffInSeconds < 604800) {
            const days = Math.floor(diffInSeconds / 86400);
            return `${days} ${days === 1 ? "day" : "days"} ago`;
        }
        if (diffInSeconds < 2592000) {
            const weeks = Math.floor(diffInSeconds / 604800);
            return `${weeks} ${weeks === 1 ? "week" : "weeks"} ago`;
        }
        const months = Math.floor(diffInSeconds / 2592000);
        return `${months} ${months === 1 ? "month" : "months"} ago`;
    }, []);

    if (workspaceModelsLoading) {
        return <LoadingScreen message="Loading models..." />;
    }

    if (workspaceModelsError) {
        return (
            <div className="flex h-full items-center justify-center bg-white p-6">
                <ErrorInfo
                    error={new Error(workspaceModelsError)}
                    retrying={false}
                    onRetryClick={() => {
                        if (currentWorkspace?.id) {
                            fetchWorkspaceModels(currentWorkspace.id, api);
                        }
                    }}
                />
            </div>
        );
    }

    return (
        <div className="flex h-full flex-col gap-6 bg-white p-6">
            {/* Header */}
            <ModelsHeader
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                totalModels={workspaceModels.length}
                filteredModels={filteredModels.length}
                workspaceUrl={currentWorkspace.url}
            />

            {/* Content - Simple scrollable grid */}
            <>
                {filteredModels.length === 0 ? (
                    <div className="flex flex-1 flex-col items-center justify-center gap-4 text-gray-500">
                        <IconFrame icon="far fa-cube" size="lg" color="gray" isCircle />
                        <p className="text-sm">
                            {searchQuery ? "No models found" : "No models yet"}
                        </p>
                    </div>
                ) : (
                    <div className="grid h-full flex-1 grid-cols-1 gap-4 overflow-y-auto sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                        {displayedModels.map((model) => (
                            <ModelCard
                                key={model.id}
                                model={model}
                                onModelClick={handleModelClick}
                                formatTimestamp={formatTimestamp}
                                onModelDelete={(model) => deleteRapidDataOnServer(model.id, api)}
                            />
                        ))}
                        {/* Invisible sentinel element for infinite scroll */}
                        {displayedCount < filteredModels.length && (
                            <div
                                ref={loadMoreRef}
                                className="col-span-full flex items-center justify-center py-8"
                            >
                                <div className="flex flex-col items-center gap-2 text-tertiary">
                                    <i className="far fa-spinner fa-spin" />
                                    <span className="text-sm">Loading more...</span>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </>
        </div>
    );
};
